import { useQuery } from '@tanstack/react-query';
import type { AuditLogDto, Paginated } from '@cpm/shared';
import { ScrollText } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Spinner,
} from '../components/ui.js';

function actionTone(action: string): 'green' | 'red' | 'blue' | 'gray' {
  if (action.endsWith('.create') || action === 'auth.setup') return 'green';
  if (action.endsWith('.delete')) return 'red';
  if (action.startsWith('auth.')) return 'blue';
  return 'gray';
}

export function AuditPage() {
  const [page, setPage] = useState(1);
  const logs = useQuery({
    queryKey: ['audit', page],
    queryFn: () => api.get<Paginated<AuditLogDto>>(`/api/audit/?page=${page}&perPage=50`),
  });

  return (
    <div>
      <PageHeader title="Audit Log" description="Every privileged action, who did it, and when." />
      <Card>
        {logs.isPending ? (
          <Spinner />
        ) : logs.error ? (
          <div className="p-5">
            <ErrorNote error={logs.error} />
          </div>
        ) : logs.data.items.length === 0 ? (
          <EmptyState
            icon={<ScrollText className="size-10" />}
            title="No audit entries yet"
            description="Actions like creating clients or changing settings are recorded here."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-3 font-medium">Time</th>
                    <th className="px-5 py-3 font-medium">User</th>
                    <th className="px-5 py-3 font-medium">Action</th>
                    <th className="px-5 py-3 font-medium">Entity</th>
                    <th className="px-5 py-3 font-medium">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {logs.data.items.map((entry) => (
                    <tr key={entry.id}>
                      <td className="whitespace-nowrap px-5 py-3 text-slate-500 dark:text-slate-400">
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                      <td className="px-5 py-3">{entry.userName ?? 'system'}</td>
                      <td className="px-5 py-3">
                        <Badge tone={actionTone(entry.action)}>{entry.action}</Badge>
                      </td>
                      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
                        {entry.entityType ?? '—'}
                      </td>
                      <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{entry.ip ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {logs.data.total > logs.data.perPage && (
              <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 text-sm dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400">
                  Page {logs.data.page} of {Math.ceil(logs.data.total / logs.data.perPage)}
                </span>
                <div className="flex gap-2">
                  <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={page >= Math.ceil(logs.data.total / logs.data.perPage)}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
